/**
 * NamespaceRow
 *
 * Single namespace row with role/members info, editability gating for admins,
 * and guarded delete confirmation. Handles various timestamp formats safely.
 */
import React, { useState } from 'react';
import { Trash2, Users, ChevronRight, Clock, AlertTriangle, Lock, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../../context/auth';
import { useNamespace } from '../../../context/namespace';

export default function NamespaceRow({ namespace, roleIcon, onClick, onDelete, currentUserRole, roleName, isEditMode = false, isDefault = false }) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const { user } = useAuth();
  const { currentNamespace } = useNamespace();
  const isAdmin = currentUserRole === 'admin';
  const isCurrentNamespace = namespace.name === currentNamespace;

  // Format the date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    try {
      // Handle different timestamp formats
      let date;
      
      // If it's a Firestore timestamp with toDate method
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } 
      // If it's a string or number, convert to Date
      else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
        date = new Date(timestamp);
      }
      // If it's already a Date object
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Default fallback
      else {
        return 'Recently';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Recently';
      }
      
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Recently';
    }
  };

  // Truncate description for display
  const getTruncatedDescription = () => {
    if (!namespace.description) return '';
    return namespace.description.length > 60 
      ? `${namespace.description.substring(0, 60)}...` 
      : namespace.description;
  };

  // Count active members
  const activeMembers = namespace.members?.filter(m => m.status === 'active')?.length || 0;
  const pendingMembers = namespace.members?.filter(m => m.status === 'pending')?.length || 0;
  const totalMembers = namespace.members?.length || 0;

  // Get the current user's status in this namespace
  const getUserStatus = () => {
    if (!user?.email || !namespace.members) return 'active'; // Default to active if no members data
    
    const userEmail = user.email.toLowerCase();
    const member = namespace.members.find(m => m.email.toLowerCase() === userEmail);
    return member?.status || 'active';
  };
  
  const userStatus = getUserStatus();
  const isPending = userStatus === 'pending';

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (isAdmin && !isDefault && !isCurrentNamespace) {
      setShowDeleteModal(true);
    }
  };

  const handleConfirmDelete = async (e) => {
    e.stopPropagation();
    if (deleteConfirmation === namespace.name) {
      setIsDeleting(true);
      try {
        await onDelete(namespace.id);
        setShowDeleteModal(false);
        setDeleteConfirmation('');
      } catch (error) {
        console.error('Error deleting namespace:', error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setShowDeleteModal(false);
    setDeleteConfirmation('');
  };

  const handleRowClick = (e) => {
    if (!isEditMode && onClick) {
      onClick(e);
    }
  };

  // Format the current user's role for display
  const displayRoleName = roleName || (currentUserRole === 'read-only' ? 'Read Only' : 
                                      currentUserRole === 'read/write' ? 'Editor' :
                                      currentUserRole.charAt(0).toUpperCase() + currentUserRole.slice(1));

  // Determine if the namespace is editable - only admins can edit and it can't be the default namespace
  const isEditable = isAdmin && !isDefault;

  // Get the appropriate tooltip message for the delete button
  const getDeleteButtonTooltip = () => {
    if (isDefault) return "Cannot delete default namespace";
    if (!isAdmin) return "Only admins can edit or delete namespaces";
    if (isCurrentNamespace) return "Cannot delete the currently active namespace";
    return `Delete ${namespace.name}`;
  };

  return (
    <>
      <div
        onClick={handleRowClick}
        className={`group relative rounded-lg border border-gray-200 bg-transparent shadow-sm transition-all ${
          !isEditMode && onClick ? 'hover:shadow-md hover:border-gray-300 cursor-pointer dark:hover:border-gray-600' : ''
        } dark:border-gray-700 dark:bg-transparent w-full mb-4`}
      >
        <div className="p-4">
          <div className="grid grid-cols-1 lg:grid-cols-[2fr,1fr,1fr,auto] gap-4">
            <div className="self-center">
              <div className="flex items-center gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30 flex-shrink-0">
                  <span className="text-lg font-medium text-purple-600 dark:text-purple-400">
                    {namespace.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="text-base font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    {namespace.name}
                    {isDefault && (
                      <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-600 dark:bg-purple-900/30 dark:text-purple-300">
                        Default
                      </span>
                    )}
                    {isCurrentNamespace && !isDefault && (
                      <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-600 dark:bg-purple-900/30 dark:text-purple-300">
                        Current
                      </span>
                    )}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {getTruncatedDescription() || 'No description'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="hidden lg:flex flex-col justify-center">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                <Users className="h-4 w-4 shrink-0" />
                <span className="font-medium">
                  {totalMembers} {totalMembers === 1 ? 'member' : 'members'}
                  {pendingMembers > 0 && ` (${pendingMembers} pending)`}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">Members</div>
            </div>
            
            <div className="hidden lg:flex flex-col justify-center">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                {roleIcon}
                <span className="font-medium">
                  {displayRoleName}
                </span>
                {isPending && (
                  <span className="ml-1 inline-flex items-center rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-600 dark:bg-purple-900/20 dark:text-purple-300">
                    <Clock className="mr-1 h-3 w-3" />
                    Pending
                  </span>
                )}
              </div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">Your Role</div>
            </div>
            
            <div className="hidden lg:flex items-center justify-end self-center">
              {isEditMode ? (
                isEditable && !isCurrentNamespace ? (
                  <button
                    onClick={handleDeleteClick}
                    className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-500 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-red-400"
                    aria-label={getDeleteButtonTooltip()}
                    title={getDeleteButtonTooltip()}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    className="rounded-full p-1.5 text-gray-300 dark:text-gray-700 cursor-not-allowed"
                    aria-label={getDeleteButtonTooltip()}
                    disabled={true}
                    title={getDeleteButtonTooltip()}
                  >
                    <Lock className="h-4 w-4" />
                  </button>
                )
              ) : !isEditable || isCurrentNamespace ? (
                <button
                  className="rounded-full p-1.5 text-gray-300 dark:text-gray-700 cursor-not-allowed"
                  aria-label={getDeleteButtonTooltip()}
                  disabled={true}
                  title={getDeleteButtonTooltip()}
                >
                  <Lock className="h-4 w-4" />
                </button>
              ) : (
                <div className="text-gray-400 group-hover:text-purple-600 dark:text-gray-600 dark:group-hover:text-purple-400">
                  {onClick && <ChevronRight className="h-5 w-5" />}
                </div>
              )}
            </div>
          </div>
          
          {/* Mobile view for members and role */}
          <div className="lg:hidden grid grid-cols-2 gap-4 mt-4 pt-3 border-t border-gray-100 dark:border-gray-800">
            <div className="flex flex-col items-start">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                <Users className="h-4 w-4 shrink-0" />
                <span className="font-medium">
                  {totalMembers} {totalMembers === 1 ? 'member' : 'members'}
                  {pendingMembers > 0 && ` (${pendingMembers} pending)`}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">Members</div>
            </div>
            
            <div className="flex flex-col items-start">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                {roleIcon}
                <span className="font-medium">
                  {displayRoleName}
                </span>
                {isPending && (
                  <span className="ml-1 inline-flex items-center rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-600 dark:bg-purple-900/20 dark:text-purple-300">
                    <Clock className="mr-1 h-3 w-3" />
                    Pending
                  </span>
                )}
              </div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">Your Role</div>
            </div>
            
            {/* Mobile Delete Button */}
            <div className="lg:hidden col-span-2 flex justify-start mt-3">
              {isEditMode ? (
                isEditable && !isCurrentNamespace ? (
                  <button
                    onClick={handleDeleteClick}
                    className="flex items-center gap-1 rounded-md px-2 py-1 text-sm text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                    aria-label={getDeleteButtonTooltip()}
                    title={getDeleteButtonTooltip()}
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Delete</span>
                  </button>
                ) : (
                  <button
                    className="flex items-center gap-1 rounded-md px-2 py-1 text-sm text-gray-300 cursor-not-allowed dark:text-gray-700"
                    aria-label={getDeleteButtonTooltip()}
                    disabled={true}
                    title={getDeleteButtonTooltip()}
                  >
                    <Lock className="h-4 w-4" />
                    <span>Delete</span>
                  </button>
                )
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={handleCancelDelete}>
          <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-900 mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex flex-col items-center text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              
              <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                Delete Namespace
              </h3>
              
              {isDefault ? (
                <div className="mt-4 flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-left">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                      Cannot Delete Default Namespace
                    </p>
                    <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-400">
                      The default namespace cannot be deleted or modified. This is your personal namespace that is automatically created for you.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    This action cannot be undone. This will permanently delete the
                    <span className="font-semibold"> {namespace.name} </span>
                    namespace and all associated data.
                  </p>
                  
                  <div className="mt-4 w-full">
                    <label htmlFor="confirm-delete" className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left mb-1">
                      Please type <span className="font-semibold">{namespace.name}</span> to confirm:
                    </label>
                    <input
                      type="text"
                      id="confirm-delete"
                      value={deleteConfirmation}
                      onChange={(e) => setDeleteConfirmation(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                      placeholder={namespace.name}
                    />
                  </div>
                </>
              )}
              
              <div className="mt-6 flex w-full space-x-3">
                <button
                  onClick={handleCancelDelete}
                  disabled={isDeleting}
                  className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                {!isDefault && (
                  <button
                    onClick={handleConfirmDelete}
                    disabled={deleteConfirmation !== namespace.name || isDeleting}
                    className="flex-1 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-red-700 dark:hover:bg-red-800 flex items-center justify-center"
                  >
                    {isDeleting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      "Delete"
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
} 